import { requireNonEmptyString } from '../row-expander/ids.js';
import { bindSort } from '../data-grid/bind-grid-events.js';
import { updateSortHeaders } from '../data-grid/render-ui.js';

const DATE_SORT_KEY = 'date';

export default {
    createPaymentDetailList(payments) {
        if (payments.length === 0) {
            return $.Deferred()
                .resolve($('<div>').addClass('payment-detail-list__empty').text('No payments recorded yet.'))
                .promise();
        }

        const totals = this.calculatePaymentTotals(payments);
        const formatDate = (createdAt) => typeof convert_date === 'function' ? convert_date(createdAt) : createdAt;
        const rows = this.sortPaymentsByDate(payments, 'desc').map((payment) => {
            const originallyOpen = !!payment.originally_open;
            const amount = this.signedLedgerAmount(payment);
            const surcharge = originallyOpen ? 0 : this.signedLedgerSurcharge(payment);
            const total = amount + surcharge;

            return {
                date: formatDate(payment.created_at),
                sort_at: String(this.paymentDateSortValue(payment)),
                reference: payment.auth_code || payment.mgr_set_paid_notes || 'N/A',
                amount: '$' + amount.toFixed(2),
                surcharge: surcharge === 0 ? 'N/A' : '$' + surcharge.toFixed(2),
                total: '$' + total.toFixed(2),
                originally_open: originallyOpen,
                is_manual: !!payment.is_manual,
                card_method: payment.card_method || null,
                card_type: payment.card_type || null,
                card_last_four: payment.card_last_four || null,
                payment_method: originallyOpen ? 'open_credit' : null,
            };
        });

        return this.fetchPaymentDetailList(rows, {
            amount: totals.amount,
            surcharge: totals.surcharge,
            grand_total: totals.grandTotal,
        });
    },

    paymentDateSortValue(payment) {
        const raw = payment.payment_date || payment.settled_at || payment.created_at;
        const ts = Date.parse(raw);
        return Number.isNaN(ts) ? 0 : ts;
    },

    sortPaymentsByDate(payments, dir) {
        const sign = dir === 'asc' ? 1 : -1;
        return [...payments].sort((left, right) => {
            return (this.paymentDateSortValue(left) - this.paymentDateSortValue(right)) * sign;
        });
    },

    bindPaymentDetailDateSort(listRoot) {
        const sheet = listRoot.querySelector('.payment-detail-sheet');
        if (!sheet) {
            return;
        }
        const state = { sortKey: DATE_SORT_KEY, sortDir: 'desc' };
        updateSortHeaders(sheet, state.sortKey, state.sortDir);
        bindSort(sheet, () => state, (key, dir) => {
            state.sortKey = key;
            state.sortDir = dir;
            updateSortHeaders(sheet, key, dir);
            const body = sheet.querySelector('.payment-detail-sheet__body');
            if (!body) {
                return;
            }
            const rows = Array.from(body.querySelectorAll('.payment-detail-sheet__row'));
            rows.sort((left, right) => {
                const cmp = (Number(left.dataset.sortValue) || 0) - (Number(right.dataset.sortValue) || 0);
                return dir === 'asc' ? cmp : -cmp;
            });
            rows.forEach((row) => body.appendChild(row));
        });
    },
    signedLedgerAmount(payment) {
        const amount = parseFloat(payment.amount) || 0;
        if (payment.is_refund) {
            return -Math.abs(amount);
        }
        if (payment.is_void || payment.status === 'failed') {
            return 0;
        }
        return amount;
    },

    signedLedgerSurcharge(payment) {
        const surcharge = parseFloat(payment.surcharge_amount) || 0;
        if (payment.is_refund) {
            return -Math.abs(surcharge);
        }
        if (payment.is_void || payment.status === 'failed') {
            return 0;
        }
        return surcharge;
    },

    calculatePaymentTotals(payments) {
        return payments.reduce((totals, payment) => {
            const originallyOpen = !!payment.originally_open;
            const surcharge = originallyOpen ? 0 : this.signedLedgerSurcharge(payment);
            const amount = this.signedLedgerAmount(payment);
            return {
                amount: totals.amount + amount,
                surcharge: totals.surcharge + surcharge,
                grandTotal: totals.grandTotal + amount + surcharge,
            };
        }, { amount: 0, surcharge: 0, grandTotal: 0 });
    },

    buildPaymentUrl(pledgeId, tableName) {
        const route = requireNonEmptyString(this.paymentDataRoute, 'paymentDataRoute');
        let url = route
            .replace(':id', pledgeId)
            .replace(':table_name', tableName);
        if (url.includes(':user_type')) {
            const userType = requireNonEmptyString(this.paymentUserType, 'paymentUserType');
            url = url.replace(':user_type', userType);
        }
        return url;
    },

    showLoadingState($paymentDetails) {
        this.setLoadingState($paymentDetails, 'Loading payment information...');
    },

    showErrorState($paymentDetails, message) {
        this.setErrorState($paymentDetails, message);
    },

    showPaymentData($paymentDetails, payments) {
        const self = this;
        self.createPaymentDetailList(payments)
            .then(($content) => {
                self.renderDetailPanelContent($paymentDetails, $content);
                self.setExpandSectionDisabled($paymentDetails, payments.length === 0);
                const listRoot = $content[0];
                if (listRoot && listRoot.querySelector) {
                    self.bindPaymentDetailDateSort(listRoot);
                }
            })
            .fail(() => self.showErrorState($paymentDetails, 'Unable to load payment details.'));
    },

    loadPaymentDetails(pledgeId, tableName) {
        if (!this.paymentDataRoute) return;
        const $paymentDetails = this.getPaymentDetailsDivById(pledgeId, tableName);
        const requestKey = `payment-${tableName || 'pledge'}-${pledgeId}`;
        this.abortRequest(requestKey);
        if (!$paymentDetails.hasClass('payment-details-loading')) {
            this.showLoadingState($paymentDetails);
        }

        let url;
        try {
            url = this.buildPaymentUrl(pledgeId, tableName);
        } catch (err) {
            console.error('loadPaymentDetails:', err);
            this.showErrorState($paymentDetails, 'Error loading payment information. Please try again.');
            return;
        }

        $.ajax({
            url: url,
            type: 'GET',
            success: (response) => {
                if (this.activeRequests[requestKey]) {
                    this.handlePaymentDetailsResponse($paymentDetails, response);
                }
            },
            error: (xhr) => {
                if (this.activeRequests[requestKey] && xhr.statusText !== 'abort') {
                    this.showErrorState($paymentDetails, 'Error loading payment information. Please try again.');
                }
            },
            complete: () => {
                delete this.activeRequests[requestKey];
            }
        });
        this.activeRequests[requestKey] = true;
    },

    handlePaymentDetailsResponse($paymentDetails, response) {
        if (!response.success) {
            this.showErrorState($paymentDetails, `Error loading payment information: ${response.error || 'Unknown error'}`);
            return;
        }
        const payments = response.data || [];
        this.showPaymentData($paymentDetails, payments);
    },
};
